import Modal from "@/components/ui/Modal";
import VideoPlayerV3 from "@/components/ui/VideoPlayerV3";
import { useGetBrandVideosQuery } from "@/redux/feature/product/productApi";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProductCard from "./ProductCard";
import VideoCardSkeleton from "./VideoCardSkeleton";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import BrandVideosCard, { BrandVideoCardSkeleton } from "./BrandVideosCard";
import AdCalenderComingSoonImage from "@/assets/images/ad-calender-coming-soon.webp";
import { CustomButton } from "@/components/ui/Buttons";
import GoToAdCalender from "./calender/GoToAdCalender";
import CalenderIcon from "@/assets/icons/Calender.svg";

const POLLING_INTERVAL = 10000;

const BrandVideos = ({
  setIsBrandModalOpen,
  selectedAudience,
  handlerOpenInsightsModal,
  handlerNavigateStructure,
  isBrandModalOpen,
  isInsightsModalOpen,
  setIsInsightsModalOpen,
}: {
  isInsightsModalOpen: boolean;
  setIsBrandModalOpen: Dispatch<SetStateAction<boolean>>;
  isBrandModalOpen: boolean;
  handlerOpenInsightsModal: () => void;
  handlerNavigateStructure: () => void;
  selectedAudience: string | null;
  setIsInsightsModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { productId } = useParams();
  const [shouldPoll, setShouldPoll] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const {
    data: brandVideos,
    isLoading: isBrandVideosLoading,
    isFetching: isBrandVideosFetching,
  } = useGetBrandVideosQuery(
    {
      productId: productId as string,
    },
    {
      pollingInterval: shouldPoll ? POLLING_INTERVAL : 0,
    }
  );

  // Track initial load completion
  useEffect(() => {
    if (!initialLoadComplete && brandVideos?.data?.length) {
      setInitialLoadComplete(true);
    }
  }, [brandVideos?.data]);

  // Stop polling when processing is complete
  useEffect(() => {
    if (brandVideos?.meta?.isProcessing === false) {
      setShouldPoll(false);
    }
  }, [brandVideos?.meta?.isProcessing]);

  const hasData =
    Array.isArray(brandVideos?.data) && brandVideos.data.length > 0;

  const brandVideosLoading =
    brandVideos?.data?.length > 0
      ? false
      : isBrandVideosLoading ||
        isBrandVideosFetching ||
        brandVideos?.meta?.isProcessing;

  return (
    <>
      <GoToAdCalender productId={productId as string} />
      {/* Main Brand Videos Container */}
      <div className="h-full flex flex-col border border-[#E4E7EC] rounded-lg p-3 sm:p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-3 sm:mb-4">
          <h2 className="text-gray-900 text-base sm:text-lg font-semibold">
            Your Ads
          </h2>
          <CustomButton
            variant="outline"
            border="1px solid #E4E7EC"
            color="#344054"
            fontWeight="500"
            padding="8px 12px"
            onClick={() => setIsBrandModalOpen(true)}
            className="whitespace-nowrap"
          >
            <span className="text-[12px]">View All Running Ads</span>
          </CustomButton>
        </div>

        {/* Video Cards Container */}
        <div className="flex flex-col gap-3 flex-1">
          {brandVideosLoading ? (
            // Show skeleton loaders during initial load
            Array.from({ length: 2 }).map((_, index) => (
              <div className="flex-1" key={index}>
                <BrandVideoCardSkeleton />
              </div>
            ))
          ) : hasData ? (
            // Show actual videos when data is available
            brandVideos.data
              .slice(0, 2)
              .map((video: VideoData, index: number) => (
                <div className="flex-1" key={video.id || index}>
                  <BrandVideosCard
                    currentlyPlaying={currentlyPlaying}
                    setCurrentlyPlaying={setCurrentlyPlaying}
                    video={video}
                  />
                </div>
              ))
          ) : (
            // Show empty state if no videos found
            <div className="flex flex-col items-center justify-center py-4 sm:py-6">
              <div className="text-center max-w-xs sm:max-w-sm">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm sm:text-base font-medium mb-2">
                  No videos available
                </p>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Your brand's video collection will appear here once content is
                  processed
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal with all videos - Responsive */}
      <Modal
        isOpen={isBrandModalOpen}
        setOpen={setIsBrandModalOpen}
        width="min(800px, 95vw)"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4 sm:p-6">
          {/* Modal Header */}
          <div className="mb-4 sm:mb-5">
            <h3 className="text-gray-900 text-lg sm:text-xl font-semibold mb-1">
              More Brand Videos
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm">
              Discover additional video content from your brand collection
            </p>
          </div>

          {/* Modal Content Grid - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {!initialLoadComplete ? (
              // Show skeleton loaders in modal during initial load
              Array.from({ length: 6 }).map((_, index) => (
                <VideoCardSkeleton key={index} />
              ))
            ) : hasData ? (
              // Show actual videos in modal
              brandVideos.data.map((video: VideoData, index: number) => (
                <div key={video.id || index}>
                  <ProductCard
                    currentlyPlaying={currentlyPlaying}
                    setCurrentlyPlaying={setCurrentlyPlaying}
                    key={video?.id}
                    video={video}
                  />
                </div>
              ))
            ) : (
              // Show empty state in modal
              <div className="col-span-full flex items-center justify-center py-8 sm:py-10">
                <p className="text-gray-500 text-sm text-center">
                  No videos found
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer - Optional pagination or additional actions */}
          {hasData && brandVideos.data.length > 12 && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Showing {Math.min(brandVideos.data.length, 12)} of{" "}
                  {brandVideos.data.length} videos
                </p>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default BrandVideos;
